// Top picks (Oct 2026, user: "could we generate some similarity based on
// recent tunes… start to detect people's preferences? basically copy Apple
// Music"): a few tunes to play next, each with the one reason it was picked.
// No service and no model — the library file carries every tune's nearest
// neighbours by its changes and how well known it is, the reader's history
// (tunes opened, saved and filed) lives in the store, and this scores one
// against the other in the browser.
//
// Pure. The rules, each from the discovery plan (docs/discovery-plan.md §4):
// - a pick must carry a SPECIFIC reason — the same changes as a tune you
//   play, the same composer, or changes close to one; a generic likeness
//   ("a medium swing like Autumn Leaves") reads as random on a big card;
// - tunes you already play are not picks;
// - the list is diversified, at most two tunes per composer, or a Wayne
//   Shorter history picks nothing but Wayne Shorter;
// - with no history the shelf is the canon, the best known tunes, rotated by
//   the week so it is the same on every device and does not reshuffle on a
//   reload.

import { normalizeName } from './libraryIdentity';

/** A tune as the library file lists it. */
export interface PickTune {
  t: string;
  c?: string;
  p?: number;
}

/** A tune you play, by index into the tunes, and how much it counts. */
export interface HistoryEntry {
  tune: number;
  weight: number;
}

export type PickReason =
  | { kind: 'same-changes'; via: number }
  | { kind: 'composer'; composer: string }
  | { kind: 'similar-changes'; via: number };

export interface TopPick {
  tune: number;
  /** Absent only for a first visit's canon. */
  reason?: PickReason;
}

export interface TopPicksInput {
  tunes: readonly PickTune[];
  /** Per tune, pairs of a neighbour's index and the similarity in percent. */
  neighbours: readonly (readonly number[])[];
  history: readonly HistoryEntry[];
  /** How many picks (6). */
  count?: number;
  /** The week the canon rotates by (`isoWeek`). */
  week?: number;
}

/** At or above: the same changes, give or take a bar (rhythm changes heads,
 * Ornithology and How High the Moon at 0.64, Corcovado under both titles). */
export const SAME_CHANGES = 0.5;
/** At or above: close enough to say so (Donna Lee and Indiana, 0.30; Blue
 * Bossa and Short Story, 0.41). Below it two tunes share only what most do. */
export const SIMILAR_CHANGES = 0.25;
/** Composers a shelf allows each, before it loosens to fill itself. */
const PER_COMPOSER = 2;
/** How far below the best known tune the canon reaches: about ninety
 * standards, the ones all three books carry with an article behind them. */
const CANON_BAND = 16;
/** How much a pick's score counts against its likeness to picks already made. */
const RELEVANCE = 0.72;
/** The ISO 8601 week of a date, the canon's weekly seed. */
export function isoWeek(date: Date): number {
  const day = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const weekday = day.getUTCDay() || 7;
  day.setUTCDate(day.getUTCDate() + 4 - weekday);
  const yearStart = Date.UTC(day.getUTCFullYear(), 0, 1);
  const week = Math.ceil(((day.getTime() - yearStart) / 86_400_000 + 1) / 7);
  return day.getUTCFullYear() * 100 + week;
}

/** A small, stable integer hash, so a weekly order needs no random numbers. */
function mix(a: number, b: number): number {
  let h = Math.imul(a ^ 0x9e3779b9, 0x85ebca6b) ^ Math.imul(b + 0x632be59b, 0xc2b2ae35);
  h = Math.imul(h ^ (h >>> 16), 0x7feb352d);
  return (h ^ (h >>> 15)) >>> 0;
}

/** Writers of a credit as written ("Miles Davis", "Bill Evans"), each with
 * the key that matches them (the split `composerCredits` makes). */
function writers(composer: string | undefined): { key: string; name: string; surname: string }[] {
  return (composer ?? '')
    .split(/[,&/]|\band\b/i)
    .map((name) => {
      const key = normalizeName(name);
      return { name: name.trim(), key, surname: key.split(' ').pop() ?? key };
    })
    .filter((writer) => writer.key);
}

function similarityLookup(neighbours: readonly (readonly number[])[]) {
  const pairs = new Map<number, Map<number, number>>();
  const add = (a: number, b: number, pct: number) => {
    let row = pairs.get(a);
    if (!row) pairs.set(a, (row = new Map()));
    row.set(b, Math.max(row.get(b) ?? 0, pct / 100));
  };
  neighbours.forEach((list, a) => {
    for (let i = 0; i + 1 < list.length; i += 2) {
      add(a, list[i], list[i + 1]);
      add(list[i], a, list[i + 1]);
    }
  });
  return {
    of: (a: number, b: number) => pairs.get(a)?.get(b) ?? 0,
    around: (a: number) => [...(pairs.get(a)?.keys() ?? [])],
  };
}

function canon(input: TopPicksInput, count: number): TopPick[] {
  const { tunes, week = 0 } = input;
  const ranked = tunes
    .map((tune, index) => ({ index, p: tune.p ?? 0 }))
    .sort((a, b) => b.p - a.p || a.index - b.index);
  // The best known tunes — within CANON_BAND of the best, and never fewer
  // than twice the shelf — in an order that changes with the week.
  const top = ranked[0]?.p ?? 0;
  const inBand = ranked.filter((tune) => tune.p >= top - CANON_BAND).length;
  const pool = ranked
    .slice(0, Math.max(inBand, count * 2))
    .sort((a, b) => mix(week, a.index) - mix(week, b.index));
  // One tune per composer, by surname, so "Jimmy" and "James" Van Heusen
  // are one songwriter.
  const used = new Set<string>();
  const picks: TopPick[] = [];
  for (const { index } of pool) {
    const keys = writers(tunes[index].c).map((w) => w.surname);
    if (keys.some((key) => used.has(key))) continue;
    keys.forEach((key) => used.add(key));
    picks.push({ tune: index });
    if (picks.length === count) break;
  }
  return picks;
}

export function topPicks(input: TopPicksInput): TopPick[] {
  const { tunes, history } = input;
  const count = input.count ?? 6;
  const played = history.filter((entry) => entry.weight > 0 && tunes[entry.tune]);
  if (played.length === 0) return canon(input, count);

  const similarity = similarityLookup(input.neighbours);
  const yours = new Set(played.map((entry) => entry.tune));
  const total = played.reduce((sum, entry) => sum + entry.weight, 0);
  // The writers of the tunes you play, how much each counts, and the name as
  // your own tune spells it (the one a reason should use).
  const writerWeight = new Map<string, number>();
  const writerName = new Map<string, string>();
  for (const entry of played) {
    for (const writer of writers(tunes[entry.tune].c)) {
      writerWeight.set(writer.key, (writerWeight.get(writer.key) ?? 0) + entry.weight);
      if (!writerName.has(writer.key)) writerName.set(writer.key, writer.name);
    }
  }

  const candidates = new Set<number>();
  for (const entry of played)
    for (const near of similarity.around(entry.tune)) candidates.add(near);
  tunes.forEach((tune, index) => {
    if (writers(tune.c).some((writer) => writerWeight.has(writer.key))) candidates.add(index);
  });

  interface Scored {
    tune: number;
    score: number;
    reason: PickReason;
    writers: string[];
  }
  const scored: Scored[] = [];
  for (const tune of candidates) {
    if (yours.has(tune)) continue;
    const own = writers(tunes[tune].c);
    let harmony = 0;
    let best = { via: -1, sim: 0, pull: 0 };
    for (const entry of played) {
      const sim = similarity.of(entry.tune, tune);
      harmony += entry.weight * sim;
      if (entry.weight * sim > best.pull) best = { via: entry.tune, sim, pull: entry.weight * sim };
    }
    const shared = own
      .map((writer) => ({ writer, weight: writerWeight.get(writer.key) ?? 0 }))
      .filter((w) => w.weight > 0)
      .sort((a, b) => b.weight - a.weight);
    const composerPull = shared.length ? shared[0].weight : 0;
    const reason: PickReason | null =
      best.sim >= SAME_CHANGES
        ? { kind: 'same-changes', via: best.via }
        : shared.length
          ? {
              kind: 'composer',
              composer: writerName.get(shared[0].writer.key) ?? shared[0].writer.name,
            }
          : best.sim >= SIMILAR_CHANGES
            ? { kind: 'similar-changes', via: best.via }
            : null;
    if (!reason) continue;
    scored.push({
      tune,
      score: (2 * harmony + 0.6 * composerPull) / total + 0.25 * ((tunes[tune].p ?? 0) / 98),
      reason,
      writers: own.map((writer) => writer.surname),
    });
  }
  scored.sort((a, b) => b.score - a.score || a.tune - b.tune);

  // Diversify: each pick weighs its own score against how much it resembles
  // the picks already made, and no composer takes more than two places —
  // unless the history leaves nothing else to pick, when the limit loosens a
  // place at a time (three Jobim tunes deserve more than two Jobim picks).
  const picks: Scored[] = [];
  const perWriter = new Map<string, number>();
  const pool = scored.slice(0, 60);
  let limit = PER_COMPOSER;
  while (picks.length < count && pool.length) {
    let bestAt = -1;
    let bestValue = -Infinity;
    pool.forEach((item, at) => {
      if (item.writers.some((key) => (perWriter.get(key) ?? 0) >= limit)) return;
      const likeness = Math.max(
        0,
        ...picks.map((pick) =>
          Math.max(
            similarity.of(pick.tune, item.tune),
            pick.writers.some((key) => item.writers.includes(key)) ? 0.5 : 0,
          ),
        ),
      );
      const value = RELEVANCE * item.score - (1 - RELEVANCE) * likeness;
      if (value > bestValue) {
        bestValue = value;
        bestAt = at;
      }
    });
    if (bestAt < 0) {
      limit++;
      continue;
    }
    const [pick] = pool.splice(bestAt, 1);
    picks.push(pick);
    for (const key of pick.writers) perWriter.set(key, (perWriter.get(key) ?? 0) + 1);
  }
  return picks.map(({ tune, reason }) => ({ tune, reason }));
}

/** Why a tune is on a More like shelf: the same or similar changes as the
 * shelf's tune, or its feel where that feel is a distinctive one. A tune by
 * the same composer needs no reason: its card names the composer. */
export type MoreLikeReason =
  | { kind: 'same-changes' }
  | { kind: 'similar-changes' }
  | { kind: 'feel'; feel: string };

export interface MoreLikeItem {
  tune: number;
  reason?: MoreLikeReason;
}

export interface MoreLikeInput {
  tunes: readonly PickTune[];
  /** Per tune, pairs of a neighbour's index and the similarity in percent. */
  neighbours: readonly (readonly number[])[];
  /** A tune's feel ("Bossa Nova"), where it has one. */
  feelOf?: (tune: number) => string | undefined;
  /** Tunes the shelf leaves out: ones already on the page. */
  exclude?: ReadonlySet<number>;
  /** How many tunes at most (10). */
  count?: number;
}

/** Places on a More like shelf for the shelf tune's own composer: enough to
 * show the composer, few enough that More like Footprints is not More by
 * Wayne Shorter. */
const OWN_COMPOSER = 4;
/** A feel shared by at most this share of the tunes that have one says
 * something (Bossa Nova, Waltz, Latin); Medium Swing, a third of the
 * library, says nothing. */
const DISTINCTIVE_FEEL = 0.08;

/**
 * More like a tune (Oct 2026, the discovery plan's "More like X" shelf). The
 * candidates each have a specific reason to be there: near the shelf tune in
 * its changes (any stored neighbour), by its composer, or sharing a
 * distinctive feel. They rank by how alike the changes are, then the
 * composer, then the feel and how well known each is, so a feel only fills
 * the shelf's end. At most four by the shelf tune's composer and two by
 * anyone else; a shelf that runs out stays short rather than loosen.
 */
export function moreLike(anchor: number, input: MoreLikeInput): MoreLikeItem[] {
  const { tunes, exclude, feelOf } = input;
  const count = input.count ?? 10;
  if (!tunes[anchor]) return [];
  const similarity = similarityLookup(input.neighbours);
  const own = writers(tunes[anchor].c);
  const ownKeys = new Set(own.map((writer) => writer.key));
  const ownSurnames = new Set(own.map((writer) => writer.surname));

  const feel = feelOf?.(anchor);
  let distinctive = false;
  const sameFeel: number[] = [];
  if (feel && feelOf) {
    let withFeel = 0;
    tunes.forEach((_, index) => {
      const theirs = feelOf(index);
      if (!theirs) return;
      withFeel++;
      if (theirs === feel) sameFeel.push(index);
    });
    distinctive = sameFeel.length <= DISTINCTIVE_FEEL * withFeel;
  }

  const candidates = new Set(similarity.around(anchor));
  if (ownKeys.size)
    tunes.forEach((tune, index) => {
      if (writers(tune.c).some((writer) => ownKeys.has(writer.key))) candidates.add(index);
    });
  if (distinctive) for (const index of sameFeel) candidates.add(index);

  interface Scored {
    tune: number;
    score: number;
    reason?: MoreLikeReason;
    writers: string[];
  }
  const scored: Scored[] = [];
  for (const tune of candidates) {
    if (tune === anchor || exclude?.has(tune) || !tunes[tune]) continue;
    const sim = similarity.of(anchor, tune);
    const theirs = writers(tunes[tune].c);
    const sameComposer = theirs.some((writer) => ownKeys.has(writer.key));
    const feelShared = !!feel && feelOf?.(tune) === feel;
    // Here for its feel alone: a distinctive one, or not here at all.
    const byFeelAlone = sim === 0 && !sameComposer;
    if (byFeelAlone && !(distinctive && feelShared)) continue;
    scored.push({
      tune,
      score:
        2 * sim +
        (sameComposer ? 0.9 : 0) +
        (feelShared ? 0.25 : 0) +
        0.25 * ((tunes[tune].p ?? 0) / 98),
      reason:
        sim >= SAME_CHANGES
          ? { kind: 'same-changes' }
          : sim >= SIMILAR_CHANGES
            ? { kind: 'similar-changes' }
            : byFeelAlone && feel
              ? { kind: 'feel', feel }
              : undefined,
      writers: theirs.map((writer) => writer.surname),
    });
  }
  scored.sort((a, b) => b.score - a.score || a.tune - b.tune);

  const shelf: Scored[] = [];
  const perWriter = new Map<string, number>();
  for (const item of scored) {
    if (shelf.length === count) break;
    const full = item.writers.some(
      (key) => (perWriter.get(key) ?? 0) >= (ownSurnames.has(key) ? OWN_COMPOSER : PER_COMPOSER),
    );
    if (full) continue;
    shelf.push(item);
    for (const key of item.writers) perWriter.set(key, (perWriter.get(key) ?? 0) + 1);
  }
  return shelf.map(({ tune, reason }) => (reason ? { tune, reason } : { tune }));
}
